import React, { useEffect, useRef, useState } from 'react';

const ProPlusWaitlistForm = () => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null); // { already?: boolean }
  const [error, setError] = useState(null);
  const [hp, setHp] = useState(''); // honeypot
  const mountedAtRef = useRef(Date.now());

  useEffect(() => {
    // view analytics
    try { window?.analytics?.track?.('proplus_waitlist_view', { path: typeof window !== 'undefined' ? window.location?.pathname : '', ts: Date.now() }); } catch(_) {}
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null); setSuccess(null);

    // min submit delay (anti-bot)
    const elapsedMs = Date.now() - (mountedAtRef.current || Date.now());
    if (elapsedMs < 800) {
      setError('Please wait a moment before submitting.');
      return;
    }
    // honeypot
    if (hp && hp.trim().length > 0) {
      // silently ignore
      return;
    }

    setLoading(true);

    // Use same-origin proxy to avoid CORS
    const endpoint = '/api/waitlist/proplus';

    try {
      try { window?.analytics?.track?.('proplus_waitlist_submit', { path: typeof window !== 'undefined' ? window.location?.pathname : '', ts: Date.now() }); } catch(_) {}
      const r = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ email, name, company, source: 'website' })
      });

      const isJson = (r.headers.get('content-type') || '').includes('application/json');
      const j = isJson ? await r.json().catch(() => ({})) : null;
      if (r.ok && (j?.success || j?.ok)) {
        setSuccess({ already: !!j?.already });
        try { window?.analytics?.track?.('proplus_waitlist_success', { already: !!j?.already, ts: Date.now() }); } catch(_) {}
        setEmail(''); setName(''); setCompany('');
      } else {
        let detail = '';
        try { detail = isJson ? (j?.error || JSON.stringify(j)) : await r.text(); } catch {}
        const msg = `Request failed (${r.status} ${r.statusText || ''})${detail ? ` — ${detail}` : ''}`.trim();
        setError(msg);
        console.error('Pro+ waitlist submission failed', { endpoint, status: r.status, statusText: r.statusText, response: detail });
        try { window?.analytics?.track?.('proplus_waitlist_error', { ts: Date.now(), status: r.status, statusText: r.statusText, message: detail || 'unknown' }); } catch(_) {}
      }
    } catch (err) {
      const message = err?.message || 'Please try again.';
      setError(`Network error: ${message}`);
      console.error('Network error submitting Pro+ waitlist', { endpoint, error: err });
      try { window?.analytics?.track?.('proplus_waitlist_error', { ts: Date.now(), message }); } catch(_) {}
    } finally {
      setLoading(false);
    }
  };

  return (
    <div id="waitlist" className="bg-card rounded-2xl border border-border p-6 sm:p-8">
      <div className="text-center mb-4">
        <h3 className="text-2xl font-bold text-foreground">Join the Pro+ Waitlist</h3>
        <p className="mt-2 text-sm text-muted-foreground">Be first in line when Pro+ opens. We’ll notify you by email.</p>
      </div>

      <form className="space-y-4" onSubmit={handleSubmit}>
        {/* Honeypot field */}
        <div className="hidden" aria-hidden="true">
          <label>Website</label>
          <input type="text" tabIndex={-1} autoComplete="off" value={hp} onChange={(e) => setHp(e.target.value)} />
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground mb-1">Email<span className="text-red-500">*</span></label>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            className="w-full rounded-lg bg-white dark:bg-background/40 border border-border px-3 py-2 text-gray-900 dark:text-foreground placeholder:text-gray-500 dark:placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-secondary/50"
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Name (optional)</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Jane Doe"
              className="w-full rounded-lg bg-white dark:bg-background/40 border border-border px-3 py-2 text-gray-900 dark:text-foreground placeholder:text-gray-500 dark:placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-secondary/50"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Company (optional)</label>
            <input
              type="text"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              placeholder="Acme Ltd"
              className="w-full rounded-lg bg-white dark:bg-background/40 border border-border px-3 py-2 text-gray-900 dark:text-foreground placeholder:text-gray-500 dark:placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-secondary/50"
            />
          </div>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full rounded-lg border border-white/80 text-white py-3 text-base font-medium transition-shadow hover:shadow-[0_0_18px_rgba(59,130,246,0.35)] disabled:opacity-60"
          aria-busy={loading ? 'true' : 'false'}
        >
          {loading ? 'Joining…' : 'Join Waitlist'}
        </button>

        <p className="text-xs text-muted-foreground">No spam. We’ll email you once invites open.</p>
      </form>

      {success && (
        <div className="mt-4 rounded-lg border border-secondary/40 bg-secondary/10 px-3 py-2 text-sm text-foreground" role="status">
          {success.already ? "You're already on the list — we’ll notify you when invites open." : "Thanks! You're on the list — we’ll notify you when invites open."}
        </div>
      )}
      {error && (
        <div className="mt-4 rounded-lg border border-red-600/40 bg-red-600/10 px-3 py-2 text-sm text-red-200" role="alert">
          {error}
        </div>
      )}
    </div>
  );
};

export default ProPlusWaitlistForm;


